/**
 * §7.3 «Пригласить / добавить сотрудника» /staff/invite (manager v3).
 *
 * Воспроизводит мокап 7.3:
 *  - форма: Электронная почта или телефон → Имя · Фамилия → Отдел → Роль
 *    (человеческий лейбл + internal-ключ в подсказке) → Смена (чипы
 *    День/Ночь/Сменно) → Способ приглашения (Электронная почта / Telegram /
 *    Скопировать ссылку);
 *  - explanation-карточка «Как сотрудник получит приглашение» (3 канала +
 *    preview TG-бота) — как в дизайне.
 *
 * Бэк: POST /v1/staff/members (createStaffMember). Привязка к выбранному отделу
 * (department_id). Роль → position_title (человеческий лейбл). Контакт/каналы —
 * UI-поля приглашения. Success → редирект в список.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateStaffMember, useStaffDepartments } from "./useStaff.js";
import { ROLE_KEY_LABEL } from "./staff-i18n.js";
import { QrCanvas } from "../guest-access/QrCanvas.js";

/**
 * Ссылка-приглашение сотруднику. SMTP не настроен, поэтому письмо не уходит —
 * менеджер копирует ссылку/QR и передаёт лично (#80). Ведёт на вход тенанта
 * с подсказкой контакта; сотрудник входит выданными кредами и завершает профиль.
 */
function buildInviteLink(contact: string): string {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://lovarthotels.com";
  const url = new URL("/login", origin);
  const c = contact.trim();
  if (c) url.searchParams.set("invite", c);
  return url.toString();
}

type ShiftPick = "day" | "night" | "rotating";

/** Роль = internal-ключ → человеческий лейбл (для дропдауна). */
const ROLE_OPTIONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "staff:housekeeping", label: ROLE_KEY_LABEL["staff:housekeeping"]! },
  { key: "staff:reception", label: ROLE_KEY_LABEL["staff:reception"]! },
  { key: "staff:kitchen", label: ROLE_KEY_LABEL["staff:kitchen"]! },
  { key: "staff:bar", label: ROLE_KEY_LABEL["staff:bar"]! },
  { key: "staff:spa", label: ROLE_KEY_LABEL["staff:spa"]! },
  { key: "staff:repair", label: ROLE_KEY_LABEL["staff:repair"]! },
  { key: "staff:concierge", label: ROLE_KEY_LABEL["staff:concierge"]! },
];

const EMPLOYMENT_BY_SHIFT: Record<ShiftPick, "full_time" | "part_time" | "contractor"> = {
  day: "full_time",
  night: "full_time",
  rotating: "part_time",
};

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function StaffInvitePage(): JSX.Element {
  const navigate = useNavigate();
  const deptsQ = useStaffDepartments();
  const create = useCreateStaffMember();

  const [contact, setContact] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [roleKey, setRoleKey] = useState<string>(ROLE_OPTIONS[0]!.key);
  const [shift, setShift] = useState<ShiftPick>("day");
  const [channels, setChannels] = useState<Set<string>>(() => new Set(["email", "telegram"]));
  // Результат создания: ссылка-приглашение для копирования/QR (#80).
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const copyLink = (link: string): void => {
    try {
      void navigator.clipboard?.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard недоступен — ссылку всё равно видно в поле */
    }
  };

  const roleLabel = useMemo(
    () => ROLE_OPTIONS.find((r) => r.key === roleKey)?.label ?? roleKey,
    [roleKey],
  );

  const canSubmit = contact.trim() !== "" && departmentId !== "";

  const toggleChannel = (c: string): void =>
    setChannels((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate(
      {
        department_id: departmentId,
        position_title: roleLabel,
        employment_type: EMPLOYMENT_BY_SHIFT[shift],
        hire_date: todayISO(),
      },
      {
        onSuccess: () => {
          // SMTP не настроен — вместо «письмо отправлено» показываем
          // копируемую ссылку-приглашение + QR (#80).
          setInviteLink(buildInviteLink(contact));
        },
      },
    );
  };

  // Результат: отдельный экран с «Назад», ссылкой и QR (canon формы).
  if (inviteLink) {
    return (
      <section data-testid="staff-invite-result" className="staff-invite-page">
        <header className="staff-topbar">
          <div>
            <button
              type="button"
              className="ta-back-btn"
              onClick={() => navigate("/staff")}
              data-testid="invite-result-back"
            >
              <span className="ta-back-btn__chev" aria-hidden="true">
                ‹
              </span>{" "}
              Назад
            </button>
            <h1 className="hsc-page__title staff-topbar__title">Приглашение создано</h1>
          </div>
        </header>

        <article className="ta-card">
          <p className="staff-invite-explain__lead">
            Почтовая отправка пока не настроена — передайте сотруднику ссылку лично
            (скопируйте или покажите QR). По ней он войдёт и завершит профиль.
          </p>

          <div className="ga-qr-wrap" data-testid="invite-qr-wrap">
            <QrCanvas value={inviteLink} size={220} testId="invite-qr" />
          </div>

          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="invite-link">
              Ссылка-приглашение
            </label>
            <div className="staff-invite-card__row">
              <input
                id="invite-link"
                className="ta-input"
                data-testid="invite-link-input"
                value={inviteLink}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                type="button"
                className="staff-btn"
                data-testid="invite-link-copy"
                onClick={() => copyLink(inviteLink)}
              >
                {copied ? "Скопировано ✓" : "Скопировать"}
              </button>
            </div>
          </div>

          <div className="staff-modal__actions">
            <button
              type="button"
              className="staff-btn staff-btn--ghost"
              onClick={() => navigate("/staff")}
            >
              К списку персонала
            </button>
          </div>
        </article>
      </section>
    );
  }

  return (
    <section data-testid="staff-invite-page" className="staff-invite-page">
      <header className="staff-topbar">
        <div>
          <div className="staff-topbar__crumbs">
            <button type="button" className="staff-link" onClick={() => navigate("/staff")}>
              Персонал
            </button>{" "}
            / Пригласить
          </div>
          <h1 className="hsc-page__title staff-topbar__title">Пригласить сотрудника</h1>
        </div>
      </header>

      <form className="ta-card staff-invite-card" onSubmit={handleSubmit} data-testid="staff-invite-form">
        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="inv-contact">Электронная почта или телефон</label>
          <input
            id="inv-contact"
            className="ta-input"
            data-testid="invite-contact"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="staff@oblaka.ru или +7 988 ···"
            required
          />
        </div>

        <div className="ta-field">
          <label className="hsc-input__label">Имя · фамилия</label>
          <div className="staff-invite-card__row">
            <input
              className="ta-input"
              data-testid="invite-firstname"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Имя"
            />
            <input
              className="ta-input"
              data-testid="invite-lastname"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Фамилия"
            />
          </div>
        </div>

        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="inv-dept">Отдел</label>
          <select
            id="inv-dept"
            className="ta-select"
            data-testid="invite-department"
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            required
          >
            <option value="">— выберите отдел —</option>
            {(deptsQ.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>

        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="inv-role">Роль</label>
          <select
            id="inv-role"
            className="ta-select"
            data-testid="invite-role"
            value={roleKey}
            onChange={(e) => setRoleKey(e.target.value)}
            title={roleKey}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.key} value={r.key} title={r.key}>
                {r.label}
              </option>
            ))}
          </select>
          <p className="staff-hint">
            Человеческое название роли. Internal-ключ <code>{roleKey}</code> — для интеграции с RBAC.
          </p>
        </div>

        <div className="ta-field">
          <label className="hsc-input__label">Смена</label>
          <div className="staff-filters">
            {([["day", "День (8:00-20:00)"], ["night", "Ночь (20:00-8:00)"], ["rotating", "Сменно (по графику)"]] as const).map(
              ([k, label]) => (
                <button
                  key={k}
                  type="button"
                  className={`ta-chip staff-filter-chip${shift === k ? " is-active" : ""}`}
                  aria-pressed={shift === k}
                  data-testid={`invite-shift-${k}`}
                  onClick={() => setShift(k)}
                >
                  {label}
                </button>
              ),
            )}
          </div>
        </div>

        <div className="ta-field">
          <label className="hsc-input__label">Способ приглашения</label>
          <div className="staff-filters">
            {([["email", "Электронная почта"], ["telegram", "Telegram"], ["link", "Скопировать ссылку"]] as const).map(
              ([k, label]) => (
                <button
                  key={k}
                  type="button"
                  className={`ta-chip staff-filter-chip${channels.has(k) ? " is-active" : ""}`}
                  aria-pressed={channels.has(k)}
                  data-testid={`invite-channel-${k}`}
                  onClick={() => toggleChannel(k)}
                >
                  {label} {channels.has(k) ? "✓" : ""}
                </button>
              ),
            )}
          </div>
        </div>

        {create.isError && (
          <p role="alert" className="staff-invite__msg staff-invite__msg--err" data-testid="invite-error">
            Не удалось отправить приглашение. Проверьте данные и попробуйте снова.
          </p>
        )}

        <div className="staff-modal__actions">
          <button type="button" className="staff-btn staff-btn--ghost" onClick={() => navigate("/staff")}>
            Отмена
          </button>
          <button type="submit" className="staff-btn" disabled={!canSubmit || create.isPending} data-testid="invite-submit">
            {create.isPending ? "Отправляем…" : "Отправить приглашение"}
          </button>
        </div>
      </form>

      {/* Explanation card — как сотрудник получит приглашение (§7.3) */}
      <article className="ta-card staff-invite-explain">
        <h3 className="staff-profile__section-title">Как сотрудник получит приглашение</h3>
        <p className="staff-invite-explain__lead">
          После создания приглашения покажется копируемая ссылка и QR — почтовая
          отправка пока не настроена, передайте ссылку сотруднику лично:
        </p>
        <div className="staff-invite-explain__grid">
          <div className="staff-invite-explain__cell">
            <div className="staff-invite-explain__cell-title">Электронная почта</div>
            <div>Письмо со ссылкой на онбординг. Тема: «Приглашение в команду LovArt».</div>
          </div>
          <div className="staff-invite-explain__cell">
            <div className="staff-invite-explain__cell-title">Telegram bot</div>
            <div>Deep-link <code>t.me/lovarthotels_bot?start=inv_…</code>. После /start бот спрашивает имя, должность, фото.</div>
          </div>
          <div className="staff-invite-explain__cell">
            <div className="staff-invite-explain__cell-title">Копия в буфер обмена</div>
            <div>Менеджер передаёт ссылку лично (например, печатает QR).</div>
          </div>
        </div>
      </article>
    </section>
  );
}
